import React, { useState } from 'react';
import {
  MessageSquare,
  Mail,
  Phone,
  Calendar,
  CheckCircle,
  AlertTriangle,
  Lock,
  ArrowUpRight,
  Settings,
  Link2,
  RefreshCw,
  Send,
  Zap,
  Globe,
  Shield,
  ExternalLink,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { cn } from '../../../lib/utils';

/**
 * Communication Settings Section
 * 
 * This is where Mailgun and Twilio technical setup lives.
 * Contains tabs for:
 * - Email Services (Mailgun)
 * - Phone System (Twilio)
 * - Calendars
 * 
 * IMPORTANT: Billing & Membership is where the agency gets access.
 * This is where the agency configures the service.
 */
const CommunicationSettingsSection = ({ agency, agencyId, entitlements, isReadOnly, navigateToSection }) => {
  const [activeTab, setActiveTab] = useState('email');
  
  // Check if services are enabled via entitlements
  const isMailgunEnabled = entitlements?.mailgun_email === 'full_access';
  const isTwilioEnabled = entitlements?.twilio_phone === 'full_access';
  const isCalendarsEnabled = entitlements?.calendars !== 'hidden';
  
  // Tabs configuration
  const tabs = [
    { key: 'email', label: 'Email Services', icon: Mail },
    { key: 'phone', label: 'Phone System', icon: Phone },
    { key: 'calendars', label: 'Calendars', icon: Calendar },
  ];
  
  return (
    <div className="space-y-6" data-testid="communication-section">
      {/* Section Header */}
      <div className="border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
            <MessageSquare className="text-cyan-600" size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Communication Settings</h2>
            <p className="text-sm text-slate-500">
              Configure email, phone, and calendar integrations
            </p>
          </div>
        </div>
      </div>
      
      {/* Service Tabs */}
      <div className="border-b border-slate-200">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={cn(
                  'flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors',
                  activeTab === tab.key
                    ? 'border-amber-500 text-amber-700 bg-amber-50/50'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                )}
              >
                <Icon size={16} />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      
      {/* Tab Content */}
      <div className="min-h-[400px]">
        {activeTab === 'email' && (
          <EmailServicesTab 
            isEnabled={isMailgunEnabled} 
            isReadOnly={isReadOnly}
            navigateToSection={navigateToSection}
          />
        )}
        {activeTab === 'phone' && (
          <PhoneSystemTab 
            isEnabled={isTwilioEnabled} 
            isReadOnly={isReadOnly}
            navigateToSection={navigateToSection}
          />
        )}
        {activeTab === 'calendars' && (
          <CalendarsTab 
            isEnabled={isCalendarsEnabled} 
            isReadOnly={isReadOnly}
          />
        )}
      </div>
    </div>
  );
};

/**
 * Email Services Tab - Mailgun Configuration
 */
const EmailServicesTab = ({ isEnabled, isReadOnly, navigateToSection }) => {
  const [testSending, setTestSending] = useState(false);
  
  if (!isEnabled) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <Lock className="text-slate-400" size={32} />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Mailgun Email Service Not Enabled
        </h3>
        <p className="text-slate-500 mb-4 max-w-md mx-auto">
          Email service requires an add-on to your current plan. Upgrade to enable custom email sending with Mailgun.
        </p>
        <Button
          className="bg-amber-500 hover:bg-amber-600 text-white"
          onClick={() => navigateToSection?.('billing')}
        >
          <ArrowUpRight size={16} className="mr-2" />
          Upgrade / Add Features
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Service Header */}
      <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Mail className="text-emerald-600" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">Mailgun Email Service</h3>
            <p className="text-sm text-emerald-700">Connected and ready to send</p>
          </div>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 border-0">
          <CheckCircle size={12} className="mr-1" />
          Connected
        </Badge>
      </div>
      
      {/* Configuration Cards */}
      <div className="grid grid-cols-2 gap-6">
        {/* API Configuration */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Settings size={16} className="text-slate-400" />
            API Configuration
          </h4>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-600">API Key</Label>
              <Input 
                type="password" 
                value="••••••••••••••••••••" 
                disabled={isReadOnly}
                className="bg-white font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Sending Domain</Label>
              <Input 
                value="mail.myagency.com" 
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
          </div>
          
          <Button variant="outline" size="sm" disabled={isReadOnly}>
            <RefreshCw size={14} className="mr-2" />
            Reconnect
          </Button>
        </div>
        
        {/* DNS Verification */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Globe size={16} className="text-slate-400" />
            DNS Verification
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-500" />
                <span className="text-sm text-emerald-700">SPF Record</span>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Verified</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <div className="flex items-center gap-2">
                <CheckCircle size={16} className="text-emerald-500" />
                <span className="text-sm text-emerald-700">DKIM Record</span>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Verified</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertTriangle size={16} className="text-amber-500" />
                <span className="text-sm text-amber-700">DMARC Record</span>
              </div>
              <Badge className="bg-amber-100 text-amber-700 border-0">Optional</Badge>
            </div>
          </div>
        </div>
      </div>
      
      {/* Sender Identity & Test */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Shield size={16} className="text-slate-400" />
            Default Sender Identity
          </h4>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-600">From Name</Label>
              <Input 
                value="My Credit Agency" 
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">From Email</Label>
              <Input 
                value="noreply@mail.myagency.com" 
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Reply-To Email</Label>
              <Input 
                value="support@myagency.com" 
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Send size={16} className="text-slate-400" />
            Test Email
          </h4>
          
          <p className="text-sm text-slate-500">
            Send a test email to verify your configuration is working correctly.
          </p>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-600">Test Recipient</Label>
              <Input 
                placeholder="test@example.com" 
                disabled={isReadOnly}
                className="bg-white"
              />
            </div>
          </div>
          
          <Button 
            variant="outline" 
            disabled={isReadOnly || testSending}
            onClick={() => {
              setTestSending(true);
              setTimeout(() => setTestSending(false), 2000);
            }}
          >
            {testSending ? (
              <>
                <RefreshCw size={14} className="mr-2 animate-spin" />
                Sending...
              </>
            ) : (
              <>
                <Send size={14} className="mr-2" />
                Send Test Email
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * Phone System Tab - Twilio Configuration
 */
const PhoneSystemTab = ({ isEnabled, isReadOnly, navigateToSection }) => {
  if (!isEnabled) {
    return (
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-8 text-center">
        <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-4">
          <Lock className="text-slate-400" size={32} />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2">
          Twilio Phone Service Not Enabled
        </h3>
        <p className="text-slate-500 mb-4 max-w-md mx-auto">
          Phone and SMS service requires an add-on to your current plan. Upgrade to enable Twilio integration.
        </p>
        <Button
          className="bg-amber-500 hover:bg-amber-600 text-white"
          onClick={() => navigateToSection?.('billing')}
        >
          <ArrowUpRight size={16} className="mr-2" />
          Upgrade / Add Features
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Service Header */}
      <div className="flex items-center justify-between p-4 bg-emerald-50 border border-emerald-200 rounded-xl">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
            <Phone className="text-emerald-600" size={24} />
          </div>
          <div>
            <h3 className="font-semibold text-emerald-900">Twilio Phone System</h3>
            <p className="text-sm text-emerald-700">Connected with SMS and voice enabled</p>
          </div>
        </div>
        <Badge className="bg-emerald-100 text-emerald-700 border-0">
          <CheckCircle size={12} className="mr-1" />
          Connected
        </Badge>
      </div>
      
      {/* Configuration Cards */}
      <div className="grid grid-cols-2 gap-6">
        {/* Account Configuration */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Settings size={16} className="text-slate-400" />
            Account Configuration
          </h4>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-600">Account SID</Label>
              <Input 
                type="password" 
                value="AC••••••••••••••••" 
                disabled={isReadOnly}
                className="bg-white font-mono"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-600">Auth Token</Label>
              <Input 
                type="password" 
                value="••••••••••••••••" 
                disabled={isReadOnly}
                className="bg-white font-mono"
              />
            </div>
          </div>
          
          <Button variant="outline" size="sm" disabled={isReadOnly}>
            <RefreshCw size={14} className="mr-2" />
            Reconnect
          </Button>
        </div>
        
        {/* Phone Numbers */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Phone size={16} className="text-slate-400" />
            Phone Numbers
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-slate-900">+1 (555) 123-4567</p>
                <p className="text-xs text-slate-500">Primary • SMS & Voice</p>
              </div>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Active</Badge>
            </div>
            
            <Button variant="outline" size="sm" disabled={isReadOnly} className="w-full">
              Add Phone Number
            </Button>
          </div>
        </div>
      </div>
      
      {/* Capabilities & Webhook */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Zap size={16} className="text-slate-400" />
            Capabilities
          </h4>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-emerald-700">SMS Sending</span>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Enabled</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-emerald-700">Voice Calls</span>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Enabled</Badge>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <span className="text-sm text-slate-600">Call Recording</span>
              <Badge className="bg-slate-100 text-slate-500 border-0">Disabled</Badge>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <h4 className="font-semibold text-slate-900 flex items-center gap-2">
            <Link2 size={16} className="text-slate-400" />
            Webhook Configuration
          </h4>
          
          <div className="space-y-3">
            <div className="space-y-2">
              <Label className="text-slate-600">Webhook URL</Label>
              <Input 
                value="https://api.fundelite.com/webhooks/twilio" 
                disabled
                className="bg-slate-50 font-mono text-xs"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-emerald-700">Webhook Status</span>
              <Badge className="bg-emerald-100 text-emerald-700 border-0">Verified</Badge>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Calendars Tab
 */
const CalendarsTab = ({ isEnabled, isReadOnly }) => {
  return (
    <div className="space-y-6">
      {/* Calendar Integrations */}
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Calendar className="text-blue-600" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Google Calendar</h4>
                <p className="text-xs text-slate-500">Sync appointments</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 border-0">Connected</Badge>
          </div>
          <Button variant="outline" size="sm" disabled={isReadOnly}>
            <Settings size={14} className="mr-2" />
            Configure
          </Button>
        </div>
        
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                <Calendar className="text-slate-400" size={20} />
              </div>
              <div>
                <h4 className="font-semibold text-slate-900">Calendly</h4>
                <p className="text-xs text-slate-500">Booking integration</p>
              </div>
            </div>
            <Badge className="bg-slate-100 text-slate-500 border-0">Not Connected</Badge>
          </div>
          <Button variant="outline" size="sm" disabled={isReadOnly}>
            <ExternalLink size={14} className="mr-2" />
            Connect
          </Button>
        </div>
      </div>
    </div>
  );
};

export default CommunicationSettingsSection;
